import React, { useState, useRef, useEffect } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import Sidebar from '@/components/Sidebar';
import { Upload, LogOut, Mail, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/components/auth/AuthContext';
import { supabase } from '@/integrations/supabase/client';

const Profile: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [subscriptionPlan, setSubscriptionPlan] = useState<string>('Free Plan');
  const { user, profile, signOut } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  useEffect(() => {
    const fetchSubscriptionPlan = async () => {
      if (!user?.id) return;

      try {
        const { data: subscriptionData, error } = await supabase
          .from('subscriptions')
          .select(`
            *,
            plan:plans (
              name
            )
          `)
          .eq('user_id', user.id)
          .eq('status', 'active')
          .order('created_at', { ascending: false })
          .limit(1)
          .single();

        if (error) {
          console.error('Error fetching subscription:', error);
          return;
        }
        
        if (subscriptionData?.plan?.name) {
          setSubscriptionPlan(subscriptionData.plan.name);
        }
      } catch (error) {
        console.error('Error in fetchSubscriptionPlan:', error);
      }
    };

    fetchSubscriptionPlan();
  }, [user?.id]);

  const handlePhotoChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !user?.id) return;

    try {
      setIsLoading(true);

      // Validate file size
      if (file.size > 2 * 1024 * 1024) {
        toast.error('File size must be less than 2MB');
        return;
      }

      // Validate file type
      if (!['image/jpeg', 'image/png', 'image/gif'].includes(file.type)) {
        toast.error('Only JPEG, PNG and GIF files are allowed');
        return;
      }

      // Upload file to Supabase storage
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}-${Date.now()}.${fileExt}`;
      
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, file, {
          cacheControl: '3600',
          upsert: true
        });

      if (uploadError) {
        console.error('Upload error:', uploadError);
        throw new Error('Error uploading file. Please try again.');
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName);

      // Update profile with new avatar URL
      const { error: updateError } = await supabase
        .from('profiles')
        .update({
          avatar_url: publicUrl,
          updated_at: new Date().toISOString()
        })
        .eq('id', user.id);

      if (updateError) {
        console.error('Profile update error:', updateError);
        throw new Error('Error updating profile. Please try again.');
      }

      toast.success('Profile photo updated successfully!');
      // Force reload to update the avatar
      window.location.reload();
    } catch (error: any) {
      toast.error(error.message || 'Error updating profile photo');
      console.error('Photo upload error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async (formType: string) => {
    setIsLoading(true);
    
    try {
      if (formType === 'Profile') {
        const firstName = (document.getElementById('firstName') as HTMLInputElement).value;
        const lastName = (document.getElementById('lastName') as HTMLInputElement).value;
        const role = (document.getElementById('jobTitle') as HTMLInputElement).value;

        // Direct update to profiles table with our fixed RLS policies
        const { error } = await supabase
          .from('profiles')
          .update({
            first_name: firstName,
            last_name: lastName,
            role: role,
            updated_at: new Date().toISOString()
          })
          .eq('id', user?.id);

        if (error) {
          console.error('Profile update error:', error);
          throw error;
        }

        console.log('Profile updated successfully');
        toast.success(`${formType} updated successfully!`);
        
        // Force reload to update the profile data
        setTimeout(() => window.location.reload(), 1000);
      } else if (formType === 'Security settings') {
        // Validate passwords
        if (!currentPassword || !newPassword || !confirmPassword) {
          toast.error('Please fill in all password fields');
          return;
        }

        if (newPassword !== confirmPassword) {
          toast.error('New passwords do not match');
          return;
        }

        if (newPassword.length < 6) {
          toast.error('New password must be at least 6 characters long');
          return;
        }

        // First verify current password
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email: user?.email || '',
          password: currentPassword
        });

        if (signInError) {
          toast.error('Current password is incorrect');
          return;
        }

        // Update password
        const { error: updateError } = await supabase.auth.updateUser({
          password: newPassword
        });

        if (updateError) {
          console.error('Password update error:', updateError);
          throw updateError;
        }

        // Clear password fields only on success
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');

        toast.success('Password updated successfully!');
      }
    } catch (error: any) {
      console.error('Error in handleSave:', error);
      toast.error(error.message || `Error updating ${formType.toLowerCase()}`);
    } finally {
      setIsLoading(false);
    }
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase();
  };

  const fullName = profile ? `${profile.first_name || ''} ${profile.last_name || ''}`.trim() : 'Update Profile';
  const initials = fullName !== 'Update Profile' ? getInitials(fullName) : 'UP';

  return (
    <div className="flex h-screen bg-background">
      <Sidebar />
      
      <main className="flex-1 overflow-y-auto">
        <div className="p-8">
          <div className="mb-8 animate-fade-in">
            <h1 className="text-3xl font-bold text-roundone-dark-purple mb-2">Your Profile</h1>
            <p className="text-muted-foreground">Manage your account settings and preferences</p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Profile sidebar */}
            <div className="lg:col-span-1 space-y-6 animate-slide-up" style={{ animationDelay: '0.1s' }}>
              <Card className="glass-card">
                <CardContent className="p-6 flex flex-col items-center text-center">
                  <Avatar className="h-24 w-24 border-4 border-roundone-light-purple/30">
                    <AvatarImage src={profile?.avatar_url || undefined} />
                    <AvatarFallback className="bg-roundone-purple text-white text-xl">{initials}</AvatarFallback>
                  </Avatar>
                  <div className="mt-4 space-y-1">
                    <h3 className="text-xl font-semibold">{fullName}</h3>
                    <p className="text-sm text-muted-foreground">{profile?.role || 'Add your role'}</p>
                    {profile?.organizations?.name && (
                      <p className="text-sm text-muted-foreground">{profile.organizations.name}</p>
                    )}
                  </div>
                  <div className="mt-6 w-full">
                    <input
                      type="file"
                      ref={fileInputRef}
                      className="hidden"
                      accept="image/*"
                      onChange={handlePhotoChange}
                    />
                    <Button 
                      variant="outline" 
                      className="w-full"
                      disabled={isLoading}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      <Upload size={16} className="mr-2" />
                      {isLoading ? "Uploading..." : "Change Photo"}
                    </Button>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="glass-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg">Account</CardTitle>
                  <CardDescription>Manage your account details</CardDescription>
                </CardHeader>
                <CardContent className="pt-4 pb-2">
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 text-sm">
                      <Mail size={16} className="text-muted-foreground" />
                      <span>{user?.email}</span>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                      <Shield size={16} className="text-muted-foreground" />
                      <span>{subscriptionPlan}</span>
                      {/* <span>Free Plan</span> */}
                    </div>
                  </div>
                </CardContent>
                <CardFooter className="pt-2">
                  <Button variant="destructive" className="w-full" size="sm" onClick={signOut}>
                    <LogOut size={16} className="mr-2" /> Sign Out
                  </Button>
                </CardFooter>
              </Card>
            </div>
            
            {/* Profile settings */}
            <div className="lg:col-span-2 animate-slide-up" style={{ animationDelay: '0.2s' }}>
              <Card className="glass-card">
                <CardHeader className="pb-2">
                  <CardTitle>Settings</CardTitle>
                  <CardDescription>Manage your profile and preferences</CardDescription>
                </CardHeader>
                <CardContent className="pt-4">
                  <Tabs defaultValue="personal">
                    <TabsList className="grid w-full grid-cols-3">
                      <TabsTrigger value="personal">Personal</TabsTrigger>
                      <TabsTrigger value="security">Security</TabsTrigger>
                      <TabsTrigger value="notifications">Notifications</TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="personal" className="py-4 space-y-6">
                      <div className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="firstName">First Name</Label>
                            <Input id="firstName" placeholder="First Name" defaultValue={profile?.first_name || ''} />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="lastName">Last Name</Label>
                            <Input id="lastName" placeholder="Last Name" defaultValue={profile?.last_name || ''} />
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="email">Email</Label>
                          <Input id="email" type="email" placeholder="Email" defaultValue={user?.email || ''} disabled />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="organization">Organization</Label>
                          <Input 
                            id="organization" 
                            placeholder="Organization" 
                            defaultValue={profile?.organizations?.name || ''} 
                            disabled 
                          />
                        </div>
                        
                        <div className="space-y-2">
                          <Label htmlFor="jobTitle">Job Title</Label>
                          <Input id="jobTitle" placeholder="Job Title" defaultValue={profile?.role || ''} />
                        </div>
                      </div>
                      
                      <Button
                        className="gradient-button w-full"
                        disabled={isLoading}
                        onClick={() => handleSave('Profile')}
                      >
                        {isLoading ? "Saving..." : "Save Changes"}
                      </Button>
                    </TabsContent>
                    
                    <TabsContent value="security" className="py-4 space-y-6">
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label htmlFor="currentPassword">Current Password</Label>
                          <Input 
                            id="currentPassword" 
                            type="password" 
                            placeholder="••••••••"
                            value={currentPassword}
                            onChange={(e) => setCurrentPassword(e.target.value)}
                          />
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div className="space-y-2">
                            <Label htmlFor="newPassword">New Password</Label>
                            <Input 
                              id="newPassword" 
                              type="password" 
                              placeholder="••••••••"
                              value={newPassword}
                              onChange={(e) => setNewPassword(e.target.value)}
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="confirmPassword">Confirm New Password</Label>
                            <Input 
                              id="confirmPassword" 
                              type="password" 
                              placeholder="••••••••"
                              value={confirmPassword}
                              onChange={(e) => setConfirmPassword(e.target.value)}
                            />
                          </div>
                        </div>
                        
                        <div className="space-y-4 pt-2">
                          <h4 className="text-sm font-medium">Two-Factor Authentication</h4>
                          <div className="flex items-center justify-between">
                            <div className="space-y-0.5">
                              <div className="text-sm">Protect your account with 2FA</div>
                              <div className="text-xs text-muted-foreground">
                                Add an extra layer of security to your account
                              </div>
                            </div>
                            <Switch />
                          </div>
                        </div>
                      </div>
                      
                      <Button
                        className="gradient-button w-full"
                        disabled={isLoading}
                        onClick={() => handleSave('Security settings')}
                      >
                        {isLoading ? "Saving..." : "Update Password"}
                      </Button>
                    </TabsContent>
                    
                    <TabsContent value="notifications" className="py-4 space-y-6">
                      <div className="space-y-4">
                        <div className="flex items-center justify-between">
                          <div className="space-y-0.5">
                            <div className="text-sm font-medium">Email Notifications</div>
                            <div className="text-xs text-muted-foreground">
                              Receive emails about assessment activity
                            </div>
                          </div>
                          <Switch defaultChecked />
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div className="space-y-0.5">
                            <div className="text-sm font-medium">Assessment Reminders</div>
                            <div className="text-xs text-muted-foreground">
                              Get reminders about incomplete assessments
                            </div>
                          </div>
                          <Switch defaultChecked />
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div className="space-y-0.5">
                            <div className="text-sm font-medium">Marketing Emails</div>
                            <div className="text-xs text-muted-foreground">
                              Receive emails about new features and updates
                            </div>
                          </div>
                          <Switch />
                        </div>
                        
                        <div className="flex items-center justify-between">
                          <div className="space-y-0.5">
                            <div className="text-sm font-medium">Weekly Summary</div>
                            <div className="text-xs text-muted-foreground">
                              Get a weekly summary of your assessment activity
                            </div>
                          </div>
                          <Switch defaultChecked />
                        </div>
                      </div>
                      
                      <Button
                        className="gradient-button w-full"
                        disabled={isLoading}
                        onClick={() => handleSave('Notification settings')}
                      >
                        {isLoading ? "Saving..." : "Save Preferences"}
                      </Button>
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Profile;
